import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, Stack, IconButton } from '@mui/material';
import { Button } from 'components/index';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Division, LineItemUnitEnum } from '../glossary/glossary.type';
import { Close } from '@mui/icons-material';
import SelectDivisionFromGiven from '../glossary/SelectDivisionFromGiven';
import SelectInput from '../SelectInput';

interface LineItemsBuldEditDialogProps {
  open: boolean;
  onClose: () => void;
  divisions: Division[];
  saveLineItem: (values: any) => Promise<void>;
}

const unitsList = Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit }));

const initialFormValues = {
  description: '',
  unit: unitsList[0].value,
  divisionId: null,
};

const AddNewLineItemDialog: React.FC<LineItemsBuldEditDialogProps> = ({ open, onClose, divisions, saveLineItem }) => {
  const [isCreating, setIsCreating] = useState(false);
  const sortedDivisions = divisions.sort((a, b) => parseInt(a.code) - parseInt(b.code));

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: yup.object({
      description: yup.string().required('Description is required'),
      unit: yup.string().required('Unit is required'),
      // divisionId: yup.string().required('Division is required'),
    }),
    onSubmit: async values => {
      setIsCreating(true);
      await saveLineItem(values);
      setIsCreating(false);
      formik.resetForm();
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Add New Line Item
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Stack spacing={2} py={2}>
            {/* Description */}
            <TextField
              label='Description'
              fullWidth
              variant='standard'
              value={formik.values.description}
              onChange={formik.handleChange}
              name='description'
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />

            {/* Division Selection */}
            <SelectDivisionFromGiven
              label='Division'
              divisions={sortedDivisions}
              selectedDivision={formik.values.divisionId}
              setSelectedDivision={divisionId => formik.setFieldValue('divisionId', divisionId)}
            />

            {/* Unit Selection */}
            <SelectInput
              label='Unit'
              selected={formik.values.unit}
              setSelected={unit => formik.setFieldValue('unit', unit)}
              list={unitsList}
            />
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' loading={isCreating} onClick={() => formik.submitForm()}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewLineItemDialog;
